/* eslint-disable no-nested-ternary */
import PropTypes from 'prop-types';
import React, { Component, PureComponent } from 'react';
import { Field, FieldArray } from 'redux-form';
import { v4 as uuid } from 'uuid';
import { RuntimeContext } from '../../../../../../common/RuntimeContext';
import BasicSelect from '../../../../../shared/components/formComponents/groups/BasicSelect';
import BasicText from '../../../../../shared/components/formComponents/groups/BasicText';
import { VerbRichTextEditor } from '../../../../../shared/components/formComponents/VerbRichTextEditor';
import VerbButton, { VERB_BUTTON_TYPE } from '../../../../../shared/components/VerbButton';
import { SECTION_ITEM_TYPES } from '../../../../../shared/Constants';
import CopyAssetLink from '../../../../components/CopyAssetLink';
import RichTextNotesHeader from './RichTextNotesHeader';

class ReviewPreviousActivitiesFieldArray extends PureComponent {
    static propTypes = {
        fields: PropTypes.any,
        contentValues: PropTypes.object,
        contentValuesKey: PropTypes.string,
        disabled: PropTypes.bool,
        getFormValue: PropTypes.func,
        handleSectionItemChange: PropTypes.func,
        populateSectionItemOptions: PropTypes.func,
        sectionItemOptionsByLearningAssetId: PropTypes.object,
        alphabeticalCollectionOptions: PropTypes.array
    };

    _pushField = () => {
        const { fields } = this.props;

        fields.push({
            id: uuid()
        });
    };

    _removeField = (index) => {
        const { fields } = this.props;

        fields.remove(index);
    };

    _moveUp = (index) => {
        const { fields } = this.props;

        if (index) {
            fields.swap(index, index - 1);
        }
    };

    _moveDown = (index) => {
        const { fields } = this.props;

        if (index < fields.length - 1) {
            fields.swap(index, index + 1);
        }
    };

    render() {
        const {
            fields,
            contentValuesKey,
            disabled,
            contentValues,
            getFormValue,
            sectionItemOptionsByLearningAssetId,
            handleSectionItemChange,
            populateSectionItemOptions,
            alphabeticalCollectionOptions
        } = this.props;

        const fieldsArray = fields.map((name, index) => {
            const sectionItemOptions =
                sectionItemOptionsByLearningAssetId[getFormValue(`${name}.learningAssetId`)] || [];
            return (
                <div className={'col-12 learning-asset-review-section'} key={contentValues[contentValuesKey][index].id}>
                    <div className={'row'}>
                        <Field
                            name={`${name}.learningAssetId`}
                            label={'Learning Asset'}
                            options={alphabeticalCollectionOptions}
                            component={BasicSelect}
                            onChange={(e, learningAssetId) => {
                                populateSectionItemOptions(learningAssetId);
                            }}
                            className={'col-3 learning-asset-id-field'}
                        />
                        <div className={'col-1 learning-asset-review-section-buttons'}>
                            <VerbButton
                                fontAwesomeIcon={'fa-angle-up'}
                                buttonType={'icon-only'}
                                onClick={() => this._moveUp(index)}
                            />
                            <VerbButton
                                fontAwesomeIcon={'fa-trash-alt'}
                                buttonType={'icon-only'}
                                onClick={() => this._removeField(index)}
                            />
                            <VerbButton
                                fontAwesomeIcon={'fa-angle-down'}
                                buttonType={'icon-only'}
                                onClick={() => this._moveDown(index)}
                            />
                        </div>
                        <Field
                            name={`${name}.label`}
                            label={'Label'}
                            className={'col-3'}
                            placeholder={'Enter a label'}
                            component={BasicText}
                        />
                        <Field
                            name={`${name}.sectionItemId`}
                            label={'Section Item'}
                            className={'col-3'}
                            placeholder={
                                !sectionItemOptions.length ? 'Choose a Learning Asset' : 'Choose a Section Item'
                            }
                            component={BasicSelect}
                            options={sectionItemOptions}
                            onChange={(e, sectionItemId) => {
                                handleSectionItemChange(name, sectionItemId);
                            }}
                        />
                    </div>
                </div>
            );
        });
        return (
            <div className={'col-12'}>
                {fieldsArray}
                <div className={'row'}>
                    {!disabled ? (
                        <VerbButton
                            onClick={this._pushField}
                            buttonType={VERB_BUTTON_TYPE.SECONDARY_DETAILS}
                            className={'spa-add-btn'}
                            fontAwesomeIcon={'fa-plus'}
                        />
                    ) : null}
                </div>
            </div>
        );
    }
}

class ReviewPreviousActivitiesSectionItem extends Component {
    constructor(props) {
        super(props);
        this.state = {
            sectionItemOptionsByLearningAssetId: [],
            sectionItemTypeBySectionItemId: {},
            alphabeticalCollectionOptions: []
        };
    }

    static propTypes = {
        assetLinkOptions: PropTypes.array,
        memberReference: PropTypes.string,
        isGlobalSectionItem: PropTypes.bool,
        getLearningAsset: PropTypes.func,
        changeFormValue: PropTypes.func,
        contentValues: PropTypes.object,
        getFormValue: PropTypes.func,
        collectionsByLearningAssetId: PropTypes.object
    };

    static contextType = RuntimeContext;

    componentDidMount() {
        const { contentValues } = this.props;
        this.buildLearningAssetFieldOptions();
        if (contentValues && contentValues.questionsToDisplay && contentValues.questionsToDisplay.length) {
            const promises = [];
            const learningAssetIdsDict = {}; // used to dedupe
            contentValues.questionsToDisplay.forEach((question) => {
                if (!learningAssetIdsDict[question.learningAssetId]) {
                    promises.push(this.populateSectionItemOptions(question.learningAssetId));
                }
                learningAssetIdsDict[question.learningAssetId] = question.learningAssetId;
            });
            return Promise.all(promises);
        }
        return null;
    }

    buildLearningAssetFieldOptions = () => {
        const { assetLinkOptions = [], collectionsByLearningAssetId = {} } = this.props;

        const newOptions = [];
        const optionNoCollection = [];
        assetLinkOptions.forEach((assetLinkOption) => {
            if (collectionsByLearningAssetId[assetLinkOption.value]) {
                collectionsByLearningAssetId[assetLinkOption.value].forEach((learningAssetOption) => {
                    newOptions.push(learningAssetOption);
                });
            } else {
                optionNoCollection.push(assetLinkOption);
            }
        });

        const alphabeticalCollectionOptions = newOptions.sort((a, b) => {
            const textA = a.label.toUpperCase();
            const textB = b.label.toUpperCase();
            return textA < textB ? -1 : textA > textB ? 1 : 0;
        });

        const alphabeticalLearningAssetOptions = optionNoCollection.sort((a, b) => {
            const textA = a.label.toUpperCase();
            const textB = b.label.toUpperCase();
            return textA < textB ? -1 : textA > textB ? 1 : 0;
        });

        alphabeticalCollectionOptions.push(...alphabeticalLearningAssetOptions);
        this.setState({
            alphabeticalCollectionOptions
        });
    };

    populateSectionItemOptions = (learningAssetId) => {
        const { getLearningAsset } = this.props;
        if (!this.state.sectionItemOptionsByLearningAssetId[learningAssetId]) {
            return getLearningAsset({ learningAssetId, pathsContext: this.context.pathsWeb })
                .then((selectedLearningAsset) => {
                    const sectionItemOptionsByLearningAssetId = { ...this.state.sectionItemOptionsByLearningAssetId };
                    const sectionItemTypeBySectionItemId = { ...this.state.sectionItemTypeBySectionItemId };
                    (selectedLearningAsset.sections || []).forEach((section) => {
                        section.sectionItems.forEach((sectionItem) => {
                            const formattedSectionItemType = sectionItem.type.toLocaleUpperCase();
                            if (
                                formattedSectionItemType === SECTION_ITEM_TYPES.CHOOSE_ONE_CONTENT ||
                                formattedSectionItemType === SECTION_ITEM_TYPES.CHOOSE_MANY_CONTENT ||
                                formattedSectionItemType === SECTION_ITEM_TYPES.LIKERT_CONTENT ||
                                formattedSectionItemType === SECTION_ITEM_TYPES.SHORT_ESSAY_CONTENT ||
                                formattedSectionItemType === SECTION_ITEM_TYPES.SHOW_PREVIOUS_ANSWERS_CHOOSE_ONE
                            ) {
                                if (!sectionItemOptionsByLearningAssetId[learningAssetId]) {
                                    sectionItemOptionsByLearningAssetId[learningAssetId] = [];
                                }
                                sectionItemOptionsByLearningAssetId[learningAssetId].push({
                                    label: `${sectionItem.name} [${sectionItem.type}]`,
                                    value: sectionItem.id
                                });
                                sectionItemTypeBySectionItemId[sectionItem.id] = sectionItem.type;
                            }
                        });
                    });
                    return { sectionItemOptionsByLearningAssetId, sectionItemTypeBySectionItemId };
                })
                .then(({ sectionItemOptionsByLearningAssetId, sectionItemTypeBySectionItemId }) =>
                    this.setState({ sectionItemOptionsByLearningAssetId, sectionItemTypeBySectionItemId })
                );
        }
        return null;
    };

    handleSectionItemChange = (fieldName, sectionItemId) => {
        const { changeFormValue } = this.props;
        changeFormValue({
            field: `${fieldName}.type`,
            value: this.state.sectionItemTypeBySectionItemId[sectionItemId]
        });
    };

    render() {
        const { memberReference, isGlobalSectionItem, assetLinkOptions, contentValues, getFormValue } = this.props;
        const { sectionItemOptionsByLearningAssetId, alphabeticalCollectionOptions } = this.state;

        return (
            <div style={{ marginBottom: '80px' }} className={'review-prev-activity-section-item-form'}>
                <div className={'row'}>
                    <div className={'row'}>
                        <Field
                            name={memberReference ? `${memberReference}.content.description` : 'content.description'}
                            label={'Description'}
                            className={'col-12 description-field'}
                            placeholder={'Enter a description'}
                            component={VerbRichTextEditor}
                            readOnly={isGlobalSectionItem}
                            rows={3}
                        />
                        <div className={'col-5'}>
                            <RichTextNotesHeader />
                            <CopyAssetLink assetLinkOptions={this.props.assetLinkOptions} />
                        </div>
                    </div>
                    <FieldArray
                        name={
                            memberReference
                                ? `${memberReference}.content.questionsToDisplay`
                                : 'content.questionsToDisplay'
                        }
                        contentValues={contentValues}
                        contentValuesKey={'questionsToDisplay'}
                        component={ReviewPreviousActivitiesFieldArray}
                        learningAssetOptions={assetLinkOptions}
                        disabled={isGlobalSectionItem}
                        populateSectionItemOptions={this.populateSectionItemOptions}
                        sectionItemOptionsByLearningAssetId={sectionItemOptionsByLearningAssetId}
                        getFormValue={getFormValue}
                        handleSectionItemChange={this.handleSectionItemChange}
                        alphabeticalCollectionOptions={alphabeticalCollectionOptions}
                    />
                </div>
            </div>
        );
    }
}

export default ReviewPreviousActivitiesSectionItem;
